import { Grid } from '@mui/material';
import { SelectableActionItem } from '../selectable-action-item';
import { SelectableItem } from '../selectable-item';
import { CreateAudienceButton } from '../create-audience-button';
import { convertToMembers, isSpecificAudienceSelected } from './utils';
import { styles } from './styles';
import { SourceEnum, TSourcedSpecificUser, TSelectedSourcedSpecificUser } from '../../../types';
import {
  PostVisibilityEnum,
  useDeleteCustomAudienceMutation,
  useLazyGetAudienceGroupAssignmentsQuery,
} from 'redux-store/api/activity-feed';
import { IAudienceOptionList } from './types';
import { useState } from 'react';

export const AudienceOptionList = ({
  projectId,
  activeAudience,
  availableAudiences,
  availableProfiles,
  onSetActiveAudience,
  onSetAudienceMembers,
  onSetAudienceToDelete,
  onOpenCustomAudienceDialog,
}: IAudienceOptionList) => {
  const [getAudienceGroupAssignments] = useLazyGetAudienceGroupAssignmentsQuery();
  const [deleteCustomAudience] = useDeleteCustomAudienceMutation();

  const [isDeletingId, setIsDeletingId] = useState<number | null>(null);

  const { postVisibility, customUsers = [] } = activeAudience || {};
  const { byRole = [], customGroups = [] } = availableAudiences || {};

  const combinedAudienceWithSource: TSourcedSpecificUser[] = [...byRole, ...customGroups];

  const isEveryoneSelected = postVisibility === PostVisibilityEnum.EVERYONE;
  const isJustMeSelected = postVisibility === PostVisibilityEnum.JUST_ME;
  const isSpecificMembersSelected =
    postVisibility === PostVisibilityEnum.SPECIFIC_MEMBERS && !customUsers.length;

  const handleEveryoneSelect = () => {
    onSetActiveAudience({
      postVisibility: PostVisibilityEnum.EVERYONE,
      byRole,
      customGroups,
      customUsers: [],
    });
  };

  const handleJustMeSelect = () => {
    onSetActiveAudience({
      postVisibility: PostVisibilityEnum.JUST_ME,
      byRole: [],
      customGroups: [],
      customUsers: [],
    });
  };

  const handleSpecificSelect = () => {
    if (isSpecificMembersSelected) return;

    onSetActiveAudience({
      postVisibility: PostVisibilityEnum.SPECIFIC_MEMBERS,
      byRole: [],
      customGroups: [],
      customUsers: [],
    });
  };

  const toggleSpecificSelection = (profile: TSourcedSpecificUser) => {
    const { source, id } = profile;
    const updatedUsers = activeAudience[source].some((p) => p.id === id)
      ? activeAudience[source].filter((p) => p.id !== id)
      : [...activeAudience[source], profile];

    onSetActiveAudience({ ...activeAudience, [source]: updatedUsers });
  };

  const handleRemoveSpecificAudience = async (profile: TSelectedSourcedSpecificUser) => {
    const { hasAssignments } = await getAudienceGroupAssignments({
      projectId,
      groupId: profile.id,
    }).unwrap();

    if (hasAssignments) {
      onSetAudienceToDelete(profile);
    } else {
      setIsDeletingId(profile.id);

      deleteCustomAudience({
        projectId,
        groupId: profile.id,
        groupIdToAssign: null,
        roleIdToAssign: 1,
      })
        .unwrap()
        .finally(() => setIsDeletingId(null));
    }
  };

  return (
    <Grid sx={styles.container}>
      <SelectableActionItem
        label="Everybody"
        onClick={handleEveryoneSelect}
        membersButtonProps={{
          count: availableProfiles?.length,
          onClick: () => {
            onSetAudienceMembers({
              title: PostVisibilityEnum.EVERYONE,
              isEveryone: true,
              members: convertToMembers(availableProfiles),
            });
          },
        }}
        toggleItemsProps={{
          isSelected: isEveryoneSelected,
          type: 'radio',
        }}
      />
      <SelectableItem
        label="Just Me"
        onClick={handleJustMeSelect}
        toggleItemsProps={{
          isSelected: isJustMeSelected,
          type: 'radio',
        }}
      />
      <Grid>
        <SelectableItem
          additionalSx={isSpecificMembersSelected ? styles.selectedSpecificMembers : undefined}
          label="Specific Members"
          onClick={handleSpecificSelect}
          toggleItemsProps={{
            isSelected: isSpecificMembersSelected,
            type: 'radio',
          }}
        />
        {isSpecificMembersSelected && (
          <>
            {combinedAudienceWithSource?.map((profile: TSourcedSpecificUser) => {
              const isSelected = isSpecificAudienceSelected(profile, activeAudience);

              return (
                <SelectableActionItem
                  isDeleting={profile.id === isDeletingId}
                  key={profile.id}
                  additionalSx={{ borderRadius: '0', borderTop: '0' }}
                  label={profile.groupName}
                  membersButtonProps={{
                    count: profile?.profiles?.length,
                    onClick: () =>
                      onSetAudienceMembers({
                        title: profile.groupName,
                        members: convertToMembers(profile?.profiles),
                      }),
                  }}
                  toggleItemsProps={{
                    isSelected,
                    type: 'checkbox',
                  }}
                  onClick={() => toggleSpecificSelection(profile)}
                  onDelete={
                    profile.source === SourceEnum.CUSTOM_GROUPS
                      ? () => handleRemoveSpecificAudience({ ...profile, isSelected })
                      : undefined
                  }
                />
              );
            })}
            <CreateAudienceButton onClick={onOpenCustomAudienceDialog} />
          </>
        )}
      </Grid>
    </Grid>
  );
};
